import PaginatedTable from '../../components/ui/PaginatedTable';
import PermissionButton from "../../components/ui/PermissionButton";
import ReportExportButton from '../../components/ui/ReportExportButton';
import FinancialScopeNotice from '../../components/ui/FinancialScopeNotice';
import TableActions from '../../components/ui/TableActions';
import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import AccountDetailsModal from "./AccountDetailsModal";
import ExportTrialBalanceModal from "./ExportTrialBalanceModal";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { Download, Eye, Search, Filter } from "lucide-react";

const COLORS = ["#facc15", "#22c55e", "#3b82f6", "#ef4444"];

export default function TrialBalance() {
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("All");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [trialBalance, setTrialBalance] = useState([]);
  const [totals, setTotals] = useState({
    debit: 0,
    credit: 0,
    difference: 0,
    accounts: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    const res = await fetch("/api/trial-balance", {
      credentials: "include",
    });

    const data = await res.json();

    if (data.status === "success") {
      setTrialBalance(data.data);
      setTotals(data.summary);
    }
  }

  const filtered = useMemo(() => {
    const term = search.toLowerCase();

    return trialBalance.filter((row) => {
      const matchesSearch =
        row.account.toLowerCase().includes(term) ||
        row.code.toLowerCase().includes(term);

      const matchesGroup = group === "All" || row.group === group;

      return matchesSearch && matchesGroup;
    });
  }, [trialBalance, search, group]);

  const chartData = useMemo(() => {
    const map = {};

    filtered.forEach((row) => {
      if (!map[row.group]) map[row.group] = 0;

      map[row.group] += row.debit + row.credit;
    });

    return Object.entries(map).map(([name, value]) => ({
      name,
      value,
    }));
  }, [filtered]);

  return (
    <DashboardLayout>
      <FinancialScopeNotice/>
      <div className="space-y-10">
        {/* Header */}

        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-serif">Trial Balance</h1>

            <p className="text-sm text-gray-400 mt-1">
              Verify debit and credit balances before preparing financial
              statements.
            </p>
          </div>

          <ReportExportButton viewPermission="accounting.trial_balance"
            onClick={() => setShowExport(true)}
            className="flex items-center gap-2 bg-amber-400 text-black px-5 py-2 rounded-xl font-semibold"
          >
            <Download size={18} />
            Export
          </ReportExportButton>
        </div>

        {/* KPI Cards */}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          <KpiCard
            title="Total Debit"
            value={`₹${totals.debit.toLocaleString()}`}
          />

          <KpiCard
            title="Total Credit"
            value={`₹${totals.credit.toLocaleString()}`}
          />

          <KpiCard
            title="Difference"
            value={`₹${Math.abs(totals.difference).toLocaleString()}`}
            danger={totals.difference !== 0}
          />

          <KpiCard title="Ledger Accounts" value={totals.accounts} />
        </div>

        {/* Summary */}

        <div
          className={`rounded-2xl border p-6 ${
            totals.difference === 0
              ? "bg-emerald-500/10 border-emerald-500/30"
              : "bg-red-500/10 border-red-500/30"
          }`}
        >
          <h3 className="font-serif text-xl mb-2">
            {totals.difference === 0
              ? "✔ Trial Balance Matched"
              : "✖ Trial Balance Not Balanced"}
          </h3>

          <p className="text-gray-300">
            Debit :
            <span className="ml-2 text-white font-semibold">
              ₹{totals.debit.toLocaleString()}
            </span>
            <span className="mx-4">|</span>
            Credit :
            <span className="ml-2 text-white font-semibold">
              ₹{totals.credit.toLocaleString()}
            </span>
          </p>
        </div>

        {/* Filters */}

        <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
          <div className="flex flex-wrap gap-4">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-3 text-gray-500"
              />

              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search account..."
                className="pl-10 pr-4 py-2 rounded-xl bg-black/40 border border-white/10"
              />
            </div>

            <select
              value={group}
              onChange={(e) => setGroup(e.target.value)}
              className="px-4 py-2 rounded-xl bg-black/40 border border-white/10"
            >
              <option>All</option>
              <option>Assets</option>
              <option>Liabilities</option>
              <option>Income</option>
              <option>Expenses</option>
            </select>

            <PermissionButton permissionModule="accounts" className="flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10 hover:bg-white/5">
              <Filter size={16} />
              More Filters
            </PermissionButton>
          </div>
        </div>

        {/* Charts */}

        <div className="grid xl:grid-cols-2 gap-8">
          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h3 className="font-serif text-lg mb-5">Accounts by Group</h3>

            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie data={chartData} dataKey="value" outerRadius={110} label>
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>

                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
            <h3 className="font-serif text-lg mb-5">Debit vs Credit</h3>

            <ResponsiveContainer width="100%" height={320}>
              <BarChart
                data={[
                  {
                    name: "Balances",
                    Debit: totals.debit,
                    Credit: totals.credit,
                  },
                ]}
              >
                <CartesianGrid stroke="#222" strokeDasharray="3 3" />

                <XAxis dataKey="name" />

                <YAxis />

                <Tooltip />

                <Bar dataKey="Debit" fill="#22c55e" radius={[8, 8, 0, 0]} />

                <Bar dataKey="Credit" fill="#facc15" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Trial Balance Table */}

        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
          <PaginatedTable className="w-full text-sm">
            <thead className="bg-black/30">
              <tr className="text-gray-400">
                <th className="text-left px-5 py-4">Code</th>

                <th className="text-left px-5 py-4">Account</th>

                <th className="text-left px-5 py-4">Group</th>

                <th className="text-right px-5 py-4">Debit</th>

                <th className="text-right px-5 py-4">Credit</th>

                <th className="text-center px-5 py-4">Status</th>

                <th className="text-center px-5 py-4">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((row) => {
                const balance = row.debit - row.credit;

                return (
                  <tr
                    key={row.id}
                    className="border-t border-white/10 hover:bg-white/5 transition"
                  >
                    <td className="px-5 py-4 font-medium">{row.code}</td>

                    <td className="px-5 py-4">{row.account}</td>

                    <td className="px-5 py-4">
                      <span className="px-3 py-1 rounded-full bg-amber-400/10 text-amber-400 text-xs">
                        {row.group}
                      </span>
                    </td>

                    <td className="px-5 py-4 text-right text-emerald-400 font-semibold">
                      {row.debit ? `₹${row.debit.toLocaleString()}` : "-"}
                    </td>

                    <td className="px-5 py-4 text-right text-red-400 font-semibold">
                      {row.credit ? `₹${row.credit.toLocaleString()}` : "-"}
                    </td>

                    <td className="px-5 py-4 text-center">
                      {balance === 0 ? (
                        <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs">
                          Balanced
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-xs">
                          Active
                        </span>
                      )}
                    </td>

                    <td className="px-5 py-4"><TableActions>
                      <div className="flex justify-center">
                        <PermissionButton permissionModule="accounts"
                          onClick={() => setSelectedAccount(row)}
                          className="p-2 rounded-lg hover:bg-white/10"
                        >
                          <Eye size={17} />
                        </PermissionButton>
                      </div>
                    </TableActions></td>
                  </tr>
                );
              })}
            </tbody>

            <tfoot className="bg-black/30 border-t border-white/10">
              <tr>
                <td colSpan={3} className="px-5 py-4 font-semibold text-right">
                  Grand Total
                </td>

                <td className="px-5 py-4 text-right font-bold text-emerald-400">
                  ₹{totals.debit.toLocaleString()}
                </td>

                <td className="px-5 py-4 text-right font-bold text-yellow-400">
                  ₹{totals.credit.toLocaleString()}
                </td>

                <td colSpan={2} className="text-center">
                  {totals.difference === 0 ? (
                    <span className="text-emerald-400 font-semibold">
                      ✔ Balanced
                    </span>
                  ) : (
                    <span className="text-red-400 font-semibold">
                      Difference ₹{Math.abs(totals.difference).toLocaleString()}
                    </span>
                  )}
                </td>
              </tr>
            </tfoot>
          </PaginatedTable>
        </div>
      </div>

      {/* Account Details */}

      {selectedAccount && (
        <AccountDetailsModal
          account={selectedAccount}
          onClose={() => setSelectedAccount(null)}
        />
      )}

      {/* Export Modal */}

      {showExport && (
        <ExportTrialBalanceModal onClose={() => setShowExport(false)} />
      )}
    </DashboardLayout>
  );
}

/* ---------- KPI CARD ---------- */

function KpiCard({ title, value, danger = false }) {
  return (
    <div
      className={`rounded-2xl p-6 border transition ${
        danger
          ? "bg-red-500/10 border-red-500/30"
          : "bg-white/5 border-white/10 hover:border-amber-400/40"
      }`}
    >
      <p className="text-xs uppercase tracking-widest text-gray-400">{title}</p>

      <h2
        className={`mt-3 text-3xl font-serif ${
          danger ? "text-red-400" : "text-white"
        }`}
      >
        {value}
      </h2>
    </div>
  );
}
